import { DButton, DIconButton } from "@dfa/ui-platform";
import { DDimensionalLogo } from "@dfa/ui-platform-icons";
import { sanitizeHTML, useDataAnalytics } from "@dfa/ui-platform-react";
import type { DAnalytics } from "@dfa/ui-platform-types";
import { useCallback } from "react";
import { parseSitecoreHTML } from "utils/parseSitecoreHTML";

type FooterButtonProps = {
	disabled?: boolean;
	label: string;
	linkURL?: never;
};

export type PromoToastProps = {
	analytics: {
		group: string;
	};
	additionalCloseLogic?(): void;
	body: string;
	closeToast?(): void; // Passed in by toastify
	cookie: {
		apiEndpoint: string;
	};
	footerButtons: [FooterButtonProps, FooterButtonProps];
	logo?:
		| true
		| {
				alt: string;
				src: string;
		  };
	title: string;
};

const PromoToast = (props: PromoToastProps) => {
	const { additionalCloseLogic, body, closeToast, footerButtons, logo, title, analytics } = props;
	const baseAnalytics: DAnalytics = {
		evt: "Link Click",
		comp: "Promo Toast"
	};

	const containerAnalytics = useDataAnalytics({
		extendedAttributes: { group: analytics?.group }
	});

	const logoNode =
		logo === true ? (
			<div className="promo-toast-logo">
				<DDimensionalLogo />
			</div>
		) : typeof logo === "object" && "src" in logo && logo.src && logo.alt ? (
			<div className="promo-toast-logo">
				<img src={logo.src} alt={logo.alt} />
			</div>
		) : null;
	const titleNode = <span className="promo-toast-title">{title}</span>;

	const handleClose = useCallback(() => {
		additionalCloseLogic?.();
		closeToast?.();
	}, [additionalCloseLogic, closeToast]);

	const Header = () => {
		return (
			<div className="promo-toast-header">
				{logoNode ?? titleNode}
				<DIconButton
					icon="close"
					size="sm"
					style="ghost"
					type="secondary"
					onClick={handleClose}
					analytics={{
						...baseAnalytics,
						name: "close",
						extendedAttributes: { group: analytics?.group }
					}}
				/>
			</div>
		);
	};

	const Body = () => {
		return (
			<div className="promo-toast-body">
				{logoNode ? titleNode : null}
				<div
					className="promo-toast-body-copy"
					dangerouslySetInnerHTML={{ __html: sanitizeHTML(body) }}
				/>
			</div>
		);
	};

	const Footer = () => {
		const [buttonOne, buttonTwo] = footerButtons;

		return (
			<div className="promo-toast-footer">
				<DButton
					analytics={{
						...baseAnalytics,
						name: buttonOne.label,
						extendedAttributes: { group: analytics?.group }
					}}
					extendedStyles={["promo-toast-footer-button"]}
					disabled={buttonOne.disabled}
					onClick={() => {
						additionalCloseLogic?.();
						closeToast?.();
					}}
					size="sm"
					style="outline"
					type="primary"
				>
					{parseSitecoreHTML(buttonOne.label)}
				</DButton>
				<DButton
					analytics={{
						...baseAnalytics,
						name: buttonTwo.label,
						extendedAttributes: { group: analytics?.group }
					}}
					extendedStyles={["promo-toast-footer-button"]}
					disabled={buttonTwo.disabled}
					onClick={() => {
						if (buttonTwo.linkURL) {
							window.location.href = buttonTwo.linkURL; // TODO: Update to DLink with button styles when spike is complete
						}
					}}
					size="sm"
					style="fill"
					type="primary"
				>
					{parseSitecoreHTML(buttonTwo.label)}
				</DButton>
			</div>
		);
	};

	return (
		<div className="promo-toast" {...containerAnalytics}>
			<Header />
			<Body />
			<Footer />
		</div>
	);
};

export default PromoToast;
